import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  buildForecastSimulationItems,
  fetchManufacturingForecastFromSales,
  saveManufacturingSimulation,
  summarizeSimulationItems,
} from "@/lib/api/manufacturingSimulation";
import { SIMULATION_SIZES } from "@/lib/constants";

const NO_SEASON = "__none__";
const cellKey = (color, size) => `${color}::${size}`;
const todayLabel = () => new Date().toLocaleDateString("es-ES");
const today = () => new Date().toISOString().slice(0, 10);
const seasonValue = (temporada) => temporada || NO_SEASON;
const seasonLabel = (value) => (value === NO_SEASON ? "(Sin temporada)" : value);

const effectivePct = (size, globalPct, perSizePct) => {
  const override = perSizePct?.[size];
  const value = override === "" || override === undefined || override === null ? globalPct : override;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const adjustQuantity = (quantity, pct) => {
  if (!pct) return quantity;
  return Math.max(0, Math.ceil(quantity * (1 + pct / 100)));
};

function buildAdjustedEntry(entry, globalPct, perSizePct) {
  const quantities = {};
  let total = 0;
  for (const color of entry.colors) {
    for (const size of SIMULATION_SIZES) {
      const base = Number(entry.quantities[cellKey(color, size)] || 0);
      if (base <= 0) continue;
      const adjusted = adjustQuantity(base, effectivePct(size, globalPct, perSizePct));
      if (adjusted <= 0) continue;
      quantities[cellKey(color, size)] = adjusted;
      total += adjusted;
    }
  }
  return { ...entry, quantities, totalQuantity: total };
}

export function SimulationForecastCapture({ onCancel, onCreated }) {
  const [loading, setLoading] = React.useState(true);
  const [forecast, setForecast] = React.useState({ matched: [], unmatched: [], discardedSizesSummary: [] });
  const [excludedIds, setExcludedIds] = React.useState(() => new Set());
  const [season, setSeason] = React.useState("");
  const [minUnits, setMinUnits] = React.useState(0);
  const [globalPct, setGlobalPct] = React.useState(0);
  const [perSizePct, setPerSizePct] = React.useState({});
  const [step, setStep] = React.useState("filters");
  const [description, setDescription] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [progress, setProgress] = React.useState({ current: 0, total: 0, code: "" });
  const { toast } = useToast();

  const loadForecast = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchManufacturingForecastFromSales();
    setLoading(false);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo cargar la previsión de fabricación", variant: "destructive" });
      return;
    }
    setForecast(result.data || { matched: [], unmatched: [], discardedSizesSummary: [] });
  }, [toast]);

  React.useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  const seasonOptions = React.useMemo(() => {
    const set = new Set((forecast.matched || []).map((entry) => seasonValue(entry.temporada)));
    return [...set].sort();
  }, [forecast.matched]);

  const adjustedEntries = React.useMemo(
    () => (forecast.matched || []).map((entry) => buildAdjustedEntry(entry, Number(globalPct) || 0, perSizePct)),
    [forecast.matched, globalPct, perSizePct]
  );

  const minThreshold = Math.max(0, Math.floor(Number(minUnits) || 0));
  const seasonEntries = season ? adjustedEntries.filter((entry) => seasonValue(entry.temporada) === season) : [];
  const keptEntries = seasonEntries.filter((entry) => entry.totalQuantity > 0 && entry.totalQuantity >= minThreshold);
  const discardedByMin = seasonEntries.filter((entry) => entry.totalQuantity > 0 && entry.totalQuantity < minThreshold);

  const seasonUnmatched = season ? (forecast.unmatched || []).filter((item) => seasonValue(item.temporada) === season) : [];
  const seasonDiscardedSizes = season ? (forecast.discardedSizesSummary || []).filter((item) => seasonValue(item.temporada) === season) : [];

  const selectedEntries = keptEntries.filter((entry) => !excludedIds.has(entry.mfgProductId));
  const isPartial = selectedEntries.length < keptEntries.length;
  const allSelected = keptEntries.length > 0 && !isPartial;
  const finalDescription = `${description.trim().replace(/\s*\(parcial\)$/i, "")}${isPartial ? " (parcial)" : ""}`;

  const toggleEntry = (id, checked) => setExcludedIds((current) => {
    const next = new Set(current);
    if (checked) next.delete(id);
    else next.add(id);
    return next;
  });

  const toggleAll = (checked) => setExcludedIds((current) => {
    const next = new Set(current);
    keptEntries.forEach((entry) => {
      if (checked) next.delete(entry.mfgProductId);
      else next.add(entry.mfgProductId);
    });
    return next;
  });

  const totals = React.useMemo(() => ({
    articles: selectedEntries.length,
    quantity: selectedEntries.reduce((sum, entry) => sum + entry.totalQuantity, 0),
  }), [selectedEntries]);

  const handlePerSizeChange = (size, value) => setPerSizePct((current) => ({ ...current, [size]: value }));

  const goToSummary = () => {
    if (!season) {
      toast({ title: "Temporada obligatoria", description: "Selecciona una temporada antes de continuar.", variant: "destructive" });
      return;
    }
    if (!selectedEntries.length) {
      toast({ title: "Nada que crear", description: "Selecciona al menos un artículo que supere los filtros para esta temporada.", variant: "destructive" });
      return;
    }
    setDescription((current) => current || `Previsión Dalia-Ennage ${seasonLabel(season)} · ${todayLabel()}`);
    setStep("summary");
  };

  const finalEntries = selectedEntries.map((entry) => ({
    mfgProductId: entry.mfgProductId,
    code: entry.code,
    description: entry.description,
    temporada: season === NO_SEASON ? "" : season,
    colors: entry.colors,
    quantities: entry.quantities,
  }));

  const createSimulation = async () => {
    if (!finalEntries.length) {
      toast({ title: "Nada que crear", description: "Selecciona al menos un artículo.", variant: "destructive" });
      return;
    }
    if (!description.trim()) {
      toast({ title: "Descripción obligatoria", description: "Introduce una descripción para la simulación.", variant: "destructive" });
      return;
    }
    setCreating(true);
    setProgress({ current: 0, total: finalEntries.length, code: "" });
    const itemsResult = await buildForecastSimulationItems(finalEntries, { onProgress: setProgress });
    if (itemsResult.error) {
      setCreating(false);
      toast({ title: "Error", description: itemsResult.error.message || "No se pudieron calcular los costes", variant: "destructive" });
      return;
    }
    const computed = summarizeSimulationItems(itemsResult.data || []);
    if (!computed.items.length) {
      setCreating(false);
      toast({ title: "Nada que crear", description: "Los artículos seleccionados no generaron cantidades.", variant: "destructive" });
      return;
    }
    const saveResult = await saveManufacturingSimulation({
      simulationDate: today(),
      description: finalDescription,
      mode: "group",
      notes: notes.trim(),
      items: computed.items,
      totals: computed.totals,
    });
    setCreating(false);
    if (saveResult.error) {
      toast({ title: "Error", description: saveResult.error.message || "No se pudo guardar la simulación", variant: "destructive" });
      return;
    }
    toast({ title: "Simulación creada", description: `${computed.items.length} artículos · ${computed.totals.total_quantity} prendas` });
    onCreated?.();
  };

  const progressPct = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border bg-white p-6 text-sm text-gray-600 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" />Cargando previsión de fabricación...
      </div>
    );
  }

  if (creating) {
    return (
      <div className="space-y-4 rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-xl font-semibold"><Loader2 className="h-5 w-5 animate-spin text-blue-600" />Creando simulación...</h2>
        <p className="text-sm text-gray-600">Procesando {progress.total} artículos. No cierres esta ventana.</p>
        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
          <div className="h-full rounded-full bg-blue-600 transition-all duration-200" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="text-sm text-gray-700">{progress.current} / {progress.total} ({progressPct}%) {progress.code ? `· ${progress.code}` : ""}</p>
      </div>
    );
  }

  if (step === "summary") {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h2 className="flex items-center gap-2 text-xl font-semibold"><Sparkles className="h-5 w-5 text-blue-600" />Informe de la simulación a crear</h2>
          <p className="text-sm text-gray-600">Revisa el resumen. Al aceptar se calcularán los costes y se creará la simulación.</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Temporada</div><div className="text-lg font-bold">{seasonLabel(season)}</div></div>
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Artículos</div><div className="text-lg font-bold">{totals.articles}</div></div>
          <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase text-gray-500">Prendas</div><div className="text-lg font-bold">{totals.quantity}</div></div>
        </div>

        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium">Descripción</label><Input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Descripción de la simulación" /></div>
            <div><label className="mb-1 block text-sm font-medium">Observaciones</label><Input value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Observaciones opcionales" /></div>
          </div>
          <p className="mt-2 text-sm text-gray-600">Descripción que se guardará: <b>{finalDescription}</b></p>
        </div>

        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold">Artículos incluidos</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead className="bg-gray-50 text-left">
                <tr><th className="border px-3 py-2">Artículo</th><th className="border px-3 py-2">Colores</th><th className="border px-3 py-2 text-right">A fabricar</th></tr>
              </thead>
              <tbody>
                {selectedEntries.map((entry) => (
                  <tr key={entry.mfgProductId}>
                    <td className="border px-3 py-2"><b>{entry.code}</b> · {entry.description}</td>
                    <td className="border px-3 py-2">{entry.colors.join(", ")}</td>
                    <td className="border px-3 py-2 text-right font-semibold">{entry.totalQuantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setStep("filters")}><ArrowLeft className="mr-2 h-4 w-4" />Volver a filtros</Button>
          <Button type="button" onClick={createSimulation}><Sparkles className="mr-2 h-4 w-4" />Crear simulación</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-semibold"><Wand2 className="h-5 w-5 text-blue-600" />Captura mágica Dalia-Ennage</h2>
            <p className="text-sm text-gray-600">Captura el pendiente de fabricar (pedidos − stock) de la gestión de pedidos Dalia-Ennage, ajústalo y crea la simulación por temporada.</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel}><ArrowLeft className="mr-2 h-4 w-4" />Volver</Button>
            <Button type="button" variant="outline" onClick={loadForecast} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" />Recargar</Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold">Filtros y ajustes</h3>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_180px]">
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Temporada (obligatoria)</label>
            <SeasonSelect data-season-selector="true"
              className="w-full rounded-md border border-input bg-background px-3 py-2"
              value={season}
              onChange={(event) => { setSeason(event.target.value); setExcludedIds(new Set()); }}
            >
              <option value="">Selecciona una temporada...</option>
              {seasonOptions.map((value) => (
                <option key={value} value={value}>{seasonLabel(value)}</option>
              ))}
            </SeasonSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Mínimo unidades / artículo</label>
            <Input type="number" min={0} value={minUnits} onChange={(event) => setMinUnits(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Incremento global %</label>
            <Input type="number" value={globalPct} onChange={(event) => setGlobalPct(event.target.value)} />
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium uppercase text-gray-500">Incremento % por talla (sobrescribe el global)</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            {SIMULATION_SIZES.map((size) => (
              <div key={size}>
                <span className="mb-1 block text-center text-xs font-semibold text-gray-600" translate="no">{size}</span>
                <Input type="number" placeholder={`${Number(globalPct) || 0}`} value={perSizePct[size] ?? ""} onChange={(event) => handlePerSizeChange(size, event.target.value)} className="text-center" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {!season ? (
        <div className="rounded-lg border border-dashed bg-white p-8 text-center text-sm text-gray-500">Selecciona una temporada para ver los artículos a capturar.</div>
      ) : (
        <>
          {seasonUnmatched.length > 0 && (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
              <div className="mb-2 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Artículos sin vincular a fabricación ({seasonUnmatched.length})</div>
              <p className="mb-2 text-xs">Necesitan fabricación pero no existen en el catálogo de fabricación (mfg_products). No se incluyen en la simulación.</p>
              <div className="flex flex-wrap gap-2">
                {seasonUnmatched.map((item) => (
                  <span key={item.salesProductId} className="rounded border border-yellow-300 bg-white px-2 py-1 text-xs">{item.code || "(sin código)"} · {item.totalMissing} u.</span>
                ))}
              </div>
            </div>
          )}

          {seasonDiscardedSizes.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
              <div className="mb-2 flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" />Tallas descartadas</div>
              <p className="mb-2 text-xs">No encajan en las 8 de simulación (P, M, L, XL, 2XL, 3XL, 4XL, 5XL) y se han ignorado.</p>
              <div className="flex flex-wrap gap-2">
                {seasonDiscardedSizes.map((item) => (
                  <span key={item.code} className="rounded border border-orange-300 bg-white px-2 py-1 text-xs">{item.code}: {item.sizes.join(", ")}</span>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">Artículos a capturar · {seasonLabel(season)}</h3>
              <div className="flex gap-4 text-sm">
                <span className="text-gray-600">Seleccionados: <b>{totals.articles} / {keptEntries.length}</b></span>
                <span className="text-gray-600">Prendas: <b>{totals.quantity}</b></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="border px-3 py-2">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={allSelected} disabled={!keptEntries.length}
                          ref={(node) => { if (node) node.indeterminate = selectedEntries.length > 0 && isPartial; }}
                          onChange={(event) => toggleAll(event.target.checked)} />
                        Seleccionar todo
                      </label>
                    </th>
                    <th className="border px-3 py-2">Artículo</th>
                    <th className="border px-3 py-2">Colores</th>
                    <th className="border px-3 py-2 text-right">A fabricar</th>
                    <th className="border px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {seasonEntries.map((entry) => {
                    const discarded = entry.totalQuantity > 0 && entry.totalQuantity < minThreshold;
                    const eligible = entry.totalQuantity > 0 && !discarded;
                    const selected = eligible && !excludedIds.has(entry.mfgProductId);
                    return (
                      <tr key={entry.mfgProductId} className={discarded ? "bg-gray-50 text-gray-400" : "bg-white"}>
                        <td className="border px-3 py-2">
                          <input type="checkbox" aria-label={`Seleccionar ${entry.code}`} checked={selected} disabled={!eligible}
                            onChange={(event) => toggleEntry(entry.mfgProductId, event.target.checked)} />
                        </td>
                        <td className="border px-3 py-2"><b>{entry.code}</b> · {entry.description}</td>
                        <td className="border px-3 py-2">{entry.colors.join(", ")}</td>
                        <td className="border px-3 py-2 text-right font-semibold">{entry.totalQuantity}</td>
                        <td className="border px-3 py-2 text-xs">{discarded ? `Descartado (< ${minThreshold})` : !eligible ? "Sin unidades" : selected ? "Incluido" : "No seleccionado"}</td>
                      </tr>
                    );
                  })}
                  {!seasonEntries.length && <tr><td className="border px-3 py-6 text-center text-gray-500" colSpan={5}>No hay artículos vinculados con pendiente de fabricar para esta temporada.</td></tr>}
                </tbody>
              </table>
            </div>
            {discardedByMin.length > 0 && <p className="mt-2 text-xs text-gray-500">{discardedByMin.length} artículo(s) descartado(s) por no alcanzar el mínimo de {minThreshold} unidades.</p>}
          </div>
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}><ArrowLeft className="mr-2 h-4 w-4" />Cancelar</Button>
        <Button type="button" onClick={goToSummary} disabled={!season || !selectedEntries.length}><Sparkles className="mr-2 h-4 w-4" />Ver informe</Button>
      </div>
    </div>
  );
}
